/**
 * Authentication functions for Feline-SA
 * Supports Email/Password and Google Sign-In
 * Includes breeder verification workflow
 */

import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * Register a new user with email/password
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} User object
 */
export const registerUser = async ({ 
  email, 
  password, 
  displayName, 
  role = 'buyer', 
  phone = '',
  // Breeder verification fields
  registryBody = null,
  registryNumber = null,
  catteryName = null,
  province = null
}) => {
  try {
    // Create auth account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    await updateProfile(user, { displayName });

    // Create user document in Firestore
    const userDoc = {
      email,
      displayName,
      role,
      phone,
      catteryId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // If registering as breeder, add verification fields
    if (role === 'breeder') {
      userDoc.breederStatus = 'pending_verification'; // pending_verification, approved, rejected
      userDoc.verificationDetails = {
        registryBody, // SACC, CASA, CFSA
        registryNumber,
        catteryName,
        province,
        submittedAt: serverTimestamp()
      };
      userDoc.verificationNotes = null;
      userDoc.verifiedAt = null;
      userDoc.verifiedBy = null;
    }

    await setDoc(doc(db, 'users', user.uid), userDoc);

    return { uid: user.uid, ...userDoc };
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Sign in with Google
 * @param {string} intendedRole - 'buyer' or 'breeder' (for new users)
 * @param {Object} breederDetails - Breeder verification details if registering as breeder
 * @returns {Promise<Object>} User object with Firestore data
 */
export const signInWithGoogle = async (intendedRole = 'buyer', breederDetails = null) => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Check if user exists in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      // Existing user - return their data
      return { uid: user.uid, ...userDoc.data(), isNewUser: false };
    } else {
      // New user - create document
      const newUserDoc = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: intendedRole,
        phone: '',
        catteryId: null,
        authProvider: 'google',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // If registering as breeder, add verification fields
      if (intendedRole === 'breeder' && breederDetails) {
        newUserDoc.breederStatus = 'pending_verification';
        newUserDoc.verificationDetails = {
          registryBody: breederDetails.registryBody,
          registryNumber: breederDetails.registryNumber,
          catteryName: breederDetails.catteryName,
          province: breederDetails.province,
          submittedAt: serverTimestamp()
        };
        newUserDoc.verificationNotes = null;
        newUserDoc.verifiedAt = null;
        newUserDoc.verifiedBy = null;
      }

      await setDoc(userDocRef, newUserDoc);

      return { uid: user.uid, ...newUserDoc, isNewUser: true };
    }
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

/**
 * Sign in existing user with email/password
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<Object>} User object with Firestore data
 */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get additional user data from Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      return { uid: user.uid, ...userDoc.data() };
    }

    return { uid: user.uid, email: user.email };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Sign out current user
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Send password reset email
 * @param {string} email 
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

/**
 * Get current user with Firestore data
 * @returns {Promise<Object|null>} User object or null
 */
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  if (!user) return null;

  const userDocRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userDocRef);

  if (userDoc.exists()) {
    return { uid: user.uid, ...userDoc.data() };
  }

  return { uid: user.uid, email: user.email };
};

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Callback function receiving user data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        callback({ uid: user.uid, ...userDoc.data() });
      } else {
        callback({ uid: user.uid, email: user.email });
      }
    } else {
      callback(null);
    }
  });
};

/**
 * Update user profile
 * @param {string} userId 
 * @param {Object} data 
 */
export const updateUserProfile = async (userId, data) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await setDoc(userDocRef, {
      ...data,
      updatedAt: serverTimestamp()
    }, { merge: true });

    // Update auth profile if displayName changed
    if (data.displayName && auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName: data.displayName });
    }
  } catch (error) {
    console.error('Profile update error:', error);
    throw error;
  }
};

/**
 * Submit breeder verification (for users who signed up as buyer first)
 * @param {string} userId 
 * @param {Object} verificationDetails 
 */
export const submitBreederVerification = async (userId, verificationDetails) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    await setDoc(userDocRef, {
      role: 'breeder',
      breederStatus: 'pending_verification',
      verificationDetails: {
        ...verificationDetails,
        submittedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Verification submission error:', error);
    throw error;
  }
};
